import { LoopingCarousel } from '@cinch-nx/shared-ui'
import { StoryblokRendererV2 as StoryblokRenderer } from '@cinch-nx/storyblok-utils'
import { SbBlokData } from '@storyblok/react'
import MarketingCard from './marketing-card/marketing-card'
import styles from './marketing-carousel.module.css'
import { useRef } from 'react'
import {
  TrackingEvent,
  TrackingEventTypes,
  useAnalytics,
} from '@cinch-nx/shared-analytics'
import { useRouter } from 'next/router'

const SESSION_STORAGE_KEY = 'marketing-carousel-tracked-impressions'

function getTrackedImpressions(): Set<string> {
  try {
    const stored = sessionStorage.getItem(SESSION_STORAGE_KEY)
    return stored ? new Set(JSON.parse(stored)) : new Set()
  } catch {
    return new Set()
  }
}

function hasAnalyticsCookieConsent(): boolean {
  try {
    const cookie = document.cookie.match('(^|;)\\s*cp\\s*=\\s*([^;]+)')?.pop()
    return cookie ? JSON.parse(cookie)?.analytics === true : false
  } catch {
    return false
  }
}

function markImpressionTracked(slideId: string): void {
  if (!hasAnalyticsCookieConsent()) return

  try {
    const tracked = getTrackedImpressions()
    tracked.add(slideId)
    sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify([...tracked]))
  } catch {
    console.warn('The sessionStorage may be unavailable.')
  }
}

interface MarketingCarouselProps {
  slides: SbBlokData[]
  slideSpeedInSeconds?: number
  shouldLoop?: boolean
}

const marketingCarouselStoryblokComponents = {
  MarketingCard,
}

function getSlideTrackingEvents(slide: SbBlokData, index: number) {
  const firstCta = Array.isArray(slide.cta) ? slide.cta[0] : undefined
  const ctaText = firstCta?.label
  const ctaDestination = firstCta?.url
  const position = index + 1

  const onClickEvent: TrackingEvent = {
    eventName: 'Carousel card click',
    type: TrackingEventTypes.ADOBE,
    data: {
      event: {
        name: 'carousel_click',
        category: 'marketing-carousel',
        label: slide.campaignName as string,
        action: 'click',
        type: 'marketing-card',
        value: String(position),
        carousel: {
          cardHeadline: slide.heading as string,
          position,
          ...(ctaText && { ctaText }),
          ...(ctaDestination && { ctaDestination }),
        },
      },
    },
  }

  const onLoadEvent: TrackingEvent = {
    eventName: 'Carousel card impression',
    type: TrackingEventTypes.ADOBE,
    data: {
      event: {
        ...onClickEvent.data.event,
        name: 'carousel_impression',
        action: 'load',
      },
    },
  }

  const linkClickTrackingEvent: TrackingEvent = {
    ...onClickEvent,
    data: {
      event: {
        ...onClickEvent.data.event,
        type: 'marketing-card-link',
      },
    },
  }

  return {
    trackingEvents: { onClick: onClickEvent, onLoad: onLoadEvent },
    linkClickTrackingEvent,
    ctaDestination,
  }
}

export default function MarketingCarousel({
  slides = [],
  slideSpeedInSeconds = 2,
  shouldLoop = true,
}: MarketingCarouselProps) {
  const router = useRouter()
  const { trackEvent } = useAnalytics()
  const trackedImpressionsRef = useRef(getTrackedImpressions())

  if (slides.length === 0) {
    return null
  }

  return (
    <LoopingCarousel
      slideSpeedInSeconds={slideSpeedInSeconds}
      shouldLoop={shouldLoop}
      swiperSettings={{
        slidesPerView: 1.1,
        spaceBetween: 12,
        pagination: { dynamicBullets: false },
        breakpoints: {
          425: { slidesPerView: 1.2 },
          514: { slidesPerView: 1.5 },
          576: { slidesPerView: 1.7 },
          722: { slidesPerView: 2.2 },
          768: { slidesPerView: 2.3 },
          869: { slidesPerView: 2.9 },
          962: { slidesPerView: 2.7 },
          1024: { slidesPerView: 1 },
        },
      }}
      className={{
        carousel: styles.carousel,
        carouselSlide: styles.carouselSlide,
        controlsWrapper: styles.controlsWrapper,
        customPagination: styles.customPagination,
      }}
    >
      {slides.map((slide, index) => {
        const { trackingEvents, linkClickTrackingEvent, ctaDestination } =
          getSlideTrackingEvents(slide, index)
        const heading = (slide.heading as string) || `Slide ${index + 1}`
        const ariaLabel = `${heading}, slide ${index + 1} of ${slides.length}`
        const slideId = (slide._uid as string) || `slide-${index}`
        const isImpressionAlreadyTracked =
          trackedImpressionsRef.current.has(slideId)
        const sessionAwareTrackingEvents = {
          ...trackingEvents,
          onLoad: isImpressionAlreadyTracked
            ? undefined
            : trackingEvents.onLoad,
        }
        const marketingCardOnClickEvent = (event: React.MouseEvent) => {
          event.stopPropagation()
          trackEvent(linkClickTrackingEvent)
        }

        return (
          <LoopingCarousel.Slide
            key={slide._uid}
            ariaLabel={ariaLabel}
            trackingEvents={sessionAwareTrackingEvents}
            onImpression={() => {
              trackedImpressionsRef.current.add(slideId)
              markImpressionTracked(slideId)
            }}
            onClick={async () => {
              if (ctaDestination) {
                await router.push(ctaDestination)
              }
            }}
          >
            <StoryblokRenderer
              block={slide}
              components={marketingCarouselStoryblokComponents}
              props={{ onClick: marketingCardOnClickEvent }}
            />
          </LoopingCarousel.Slide>
        )
      })}
    </LoopingCarousel>
  )
}
